// WorkflowPerformerRoles.tsx

import { useState } from "react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Check, X, Plus, Trash2, SquarePen } from "lucide-react";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { AlertDialog } from "@/widgets/AlertDailog";
import { PaginationControl } from "@/widgets/renderPagination";
import { useLocation } from "wouter";
import { Popup } from "@/widgets/RuleDetailDailog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { deleteWorkflowRole, useWorkflowPerformerRoles } from "@/hooks/useLocalStorage";
import { WorkflowTeamsAction } from "@/lib/api-services";
import { DeleteConfirmationDialog } from "@/widgets/DeleteModal";
import { parseApiErrorMessage } from "@/hooks/parseApiErrorMessage";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { useBreadcrumbs } from "../BreadcrumbContext";
import { AddWorkflowPerformerRole } from "./AddWorkflowPerformerRole";

interface TableHierarchicalNodeProps {
    topLevelData: WorkflowTeamsAction[];
    pageIndex: number;
    pageSize: number;
    totalCount: number;
    onPageChange: (page: number) => void;
    onEditForm: (data: any) => void;
    onAlert: (message: string, status: boolean) => void;
}

function TableForm({
    topLevelData,
    pageIndex,
    pageSize,
    totalCount,
    onPageChange,
    onEditForm,
    onAlert,
}: TableHierarchicalNodeProps) {
    const [currentData, setCurrentData] = useState<WorkflowTeamsAction[]>([]);

    const [showPopup, setShowPopup] = React.useState(false);
    const [popupName, setPopupName] = React.useState("");
    const [headingData, setHeadingData] = React.useState<string | null>("");
    const [descriptionContent, setDescriptionContent] = React.useState<string | null>("");

    const [_location, setLocation] = useLocation();

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<WorkflowTeamsAction | null>(null);

    const deletePortalRole = deleteWorkflowRole();

    React.useEffect(() => {
        if (topLevelData) setCurrentData(topLevelData);
    }, [topLevelData]);

    function handleDeleteClick(e: React.MouseEvent, item: WorkflowTeamsAction) {
        e.stopPropagation();
        setItemToDelete(item);
        setDeleteDialogOpen(true);
    }

    function handleConfirmDelete() {
        if (!itemToDelete) return;
        deletePortalRole.mutate(itemToDelete.id);
    }

    React.useEffect(() => {
        if (deletePortalRole.isSuccess) {
            setDeleteDialogOpen(false);
            setItemToDelete(null);
            onAlert("Workflow Performer Role deleted successfully!", true);
            deletePortalRole.reset();
        } else if (deletePortalRole.isError) {
            const raw = deletePortalRole.error as Error;
            setDeleteDialogOpen(false);
            onAlert(parseApiErrorMessage(raw.message), false);
            deletePortalRole.reset();
        }
    }, [deletePortalRole.isSuccess, deletePortalRole.isError]);

    function handleViewDescription(item: WorkflowTeamsAction) {
        setPopupName("Description");
        setHeadingData(item.roleName);
        setDescriptionContent(item.description);
        setShowPopup(true);
    }

    return (
        <div>
            <div className="border bg-white border-gray-200 rounded-lg p-2">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Id</TableHead>
                            <TableHead>Role Type</TableHead>
                            <TableHead>Role Name</TableHead>
                            <TableHead>Total Entry</TableHead>
                            <TableHead>Decrypt Data</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Created Time</TableHead>
                            <TableHead>Last Modified</TableHead>
                            <TableHead>Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {currentData?.map((data) => (
                            <TableRow key={data.id}>
                                <TableCell className="">{data.id}</TableCell>
                                <TableCell className="">{data.roleTypeName}</TableCell>
                                <TableCell className="">{data.roleName}</TableCell>
                                <TableCell className="">
                                    {data.userPermission ? data.totalEntry : "Unrestricted"}
                                </TableCell>
                                <TableCell className="">
                                    {data.decryptData ? (
                                        <Check className="h-5 w-5 mr-1 text-green-800 font-bold stroke-[2.5]" />
                                    ) : (
                                        <X className="h-5 w-5 mr-1 text-red-800 font-bold stroke-[2.5]" />
                                    )}
                                </TableCell>
                                <TableCell className="max-w-xs truncate">
                                    <Button variant="link" onClick={() => handleViewDescription(data)}>
                                        View
                                    </Button>
                                </TableCell>
                                <TableCell className="max-w-xs">
                                    <div className="flex flex-col">
                                        <span>
                                            {new Date(data.dateTimeAdded).toLocaleDateString("en-IN", {
                                                day: "2-digit", month: "short", year: "numeric",
                                            })}
                                        </span>
                                        <span className="text-xs text-black">
                                            {new Date(data.dateTimeAdded).toLocaleTimeString("en-IN", {
                                                hour: "2-digit", minute: "2-digit",
                                            })}
                                        </span>
                                    </div>
                                </TableCell>
                                <TableCell className="max-w-xs">
                                    <div className="flex flex-col">
                                        {data.lastModified ? (
                                            <>
                                                <span>
                                                    {new Date(data.lastModified).toLocaleDateString("en-IN", {
                                                        day: "2-digit", month: "short", year: "numeric",
                                                    })}
                                                </span>
                                                <span className="text-xs text-black">
                                                    {new Date(data.lastModified).toLocaleTimeString("en-IN", {
                                                        hour: "2-digit", minute: "2-digit",
                                                    })}
                                                </span>
                                            </>
                                        ) : "-"}
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <div className="flex flex-row items-center space-x-2">
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="sm" className="min-w-6 h-6 p-0" tooltip="More actions">
                                                    <Plus className="h-3 w-3" />
                                                    <span className="sr-only">More actions</span>
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="min-w-[180px]">
                                                <DropdownMenuItem
                                                    className="flex items-center px-1 cursor-pointer"
                                                    onClick={() => onEditForm(data)}
                                                >
                                                    <SquarePen className="h-5 w-5 mr-2 stroke-[2.5]" />
                                                    <span className="text-sm">Edit Form</span>
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                    onClick={(e) => handleDeleteClick(e, data)}
                                                    className="flex items-center px-1 cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                                                >
                                                    <Trash2 className="h-5 w-5 mr-2 stroke-[2.5]" />
                                                    <span className="text-sm">Delete</span>
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                <div className="mt-2">
                    <PaginationControl
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        totalCount={totalCount}
                        onPageChange={onPageChange}
                    />
                </div>
            </div>

            {showPopup && (
                <Popup
                    popupName={popupName}
                    heading={headingData}
                    content={descriptionContent}
                    onClose={() => setShowPopup(false)}
                />
            )}

            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                onConfirm={handleConfirmDelete}
                isPending={deletePortalRole.isPending}
                itemName={itemToDelete as any}
                itemType="Workflow Performer Role"
                requireAuthCode={false}
            />

        </div>
    );
}


export function WorkflowPerformerRoles() {
    const { setBreadcrumbs } = useBreadcrumbs();
    const testdata = [
        {
            "id": 15,
            "roleType": 6103,
            "roleTypeType": "WF Role Type",
            "roleTypeName": "Inspectors",
            "roleName": "Reg role",
            "userPermission": false,
            "totalEntry": null,
            "decryptData": false,
            "description": "Role Description",
            "dateTimeAdded": "2026-04-22T12:21:28.032101",
            "lastModified": null
        },
    ]

    const [topLevelTypes, setTopLevelTypes] = useState<WorkflowTeamsAction[]>([]);
    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const [filterOpen, setFilterOpen] = useState(false);

    const [wfIdInput, setWfIdInput] = useState<string>("");
    const [wfNameInput, setWfNameInput] = useState<string>("");
    const [wfStatusInput, setWfStatusInput] = useState<string>("");

    const [appliedWfId, setAppliedWfId] = useState<string | undefined>();
    const [appliedWfName, setAppliedWfName] = useState<string | undefined>();
    const [appliedWfStatus, setAppliedWfStatus] = useState<string | undefined>();

    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [editFormData, setEditFormData] = useState<any>(null);

    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [reqStatus, setReqStatus] = useState(false);

    const { data: types, isLoading } = useWorkflowPerformerRoles(
        pageIndex,
        pageSize,
        appliedWfId,
        appliedWfName,
        appliedWfStatus
    );

    React.useEffect(() => {
        setBreadcrumbs([{ label: "Performer Teams", path: `/workflowPerformer` }]);
    }, []);

    React.useEffect(() => {
        setTopLevelTypes(types?.items ?? []);
        setTotalCount(types?.total ?? 0);
    }, [types]);

    const handleSearch = () => {
        setAppliedWfId(wfIdInput.trim() || undefined);
        setAppliedWfName(wfNameInput.trim() || undefined);
        setAppliedWfStatus(wfStatusInput || undefined);
        setPageIndex(1);
    };

    const handleRefresh = (close: boolean) => {
        setWfIdInput("");
        setWfNameInput("");
        setWfStatusInput("");
        setAppliedWfId(undefined);
        setAppliedWfName(undefined);
        setAppliedWfStatus(undefined);
        setPageIndex(1);
        if (close) setFilterOpen(false);
    };

    const handleAddForm = () => {
        setEditFormData(null);
        setOpenCreateDialog(true);
    };

    const handleEditForm = (data: any) => {
        setEditFormData(data);
        setOpenCreateDialog(true);
    };

    const handleAlert = (message: string, status: boolean) => {
        setAlertMessage(message);
        setReqStatus(status);
        setAlertOpen(true);
    };

    return (
        <div className="min-h-full">
            <div className="mb-4 md:mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                    <div className="min-w-0">
                        <h2 className="text-lg md:text-xl font-semibold text-black">
                            Manage Performer Roles
                        </h2>
                        <p className="text-black mt-1 text-sm md:text-base">
                            Add or modify workflow performer roles
                        </p>
                    </div>
                    <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
                        <Button
                            className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
                            size="sm"
                            onClick={() => setFilterOpen((prev) => !prev)}
                        >
                            Filter Performer
                        </Button>
                        <Button
                            className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
                            size="sm"
                            onClick={handleAddForm}
                        >
                            <Plus className="h-3 w-3 md:h-4 md:w-4 mr-2" />
                            Add Performer Roles
                        </Button>
                    </div>
                </div>
            </div>

            {filterOpen && (
                <Card className="mb-4 md:mb-6">
                    <CardContent className="pt-6">
                        <div className="flex flex-col md:flex-row md:items-end md:space-x-4 space-y-3 md:space-y-0">
                            <div className="flex-1">
                                <label className="text-sm font-medium mb-1.5 block">Id</label>
                                <Input
                                    placeholder="Enter Id"
                                    value={wfIdInput}
                                    onChange={(e) => setWfIdInput(e.target.value)}
                                />
                            </div>
                            <div className="flex-1">
                                <label className="text-sm font-medium mb-1.5 block">Role Name</label>
                                <Input
                                    placeholder="Enter Role Name"
                                    value={wfNameInput}
                                    onChange={(e) => setWfNameInput(e.target.value)}
                                />
                            </div>

                            <div className="flex-1">
                                <label className="text-sm font-medium mb-1.5 block">User Permission</label>
                                <Select
                                    value={wfStatusInput}
                                    onValueChange={(value) => setWfStatusInput(value === "all" ? "" : value)}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select Permission" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All</SelectItem>
                                        <SelectItem value="true">Restricted</SelectItem>
                                        <SelectItem value="false">Unrestricted</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="mt-4 flex justify-center space-x-2">
                            <Button onClick={handleSearch}>Search</Button>
                            <Button variant="outline" onClick={() => handleRefresh(false)}>
                                Refresh
                            </Button>
                            <Button variant="outline" onClick={() => handleRefresh(true)}>
                                Close
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {!isLoading ? (
                <Card>
                    <CardContent className="p-4 md:p-6">
                        <div className="space-y-2">
                            {topLevelTypes && topLevelTypes.length > 0 ? (
                                <TableForm
                                    topLevelData={topLevelTypes}
                                    pageIndex={pageIndex}
                                    pageSize={pageSize}
                                    totalCount={totalCount}
                                    onPageChange={setPageIndex}
                                    onEditForm={handleEditForm}
                                    onAlert={handleAlert}
                                />
                            ) : (
                                <div className="text-center py-8 text-black">
                                    <p>No Workflow found. Create one to get started.</p>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            ) : (
                <div className="p-6">Loading...</div>
            )}

            {openCreateDialog && (
                <AddWorkflowPerformerRole
                    open={openCreateDialog}
                    onOpenChange={setOpenCreateDialog}
                    initialData={editFormData}
                    onAlert={handleAlert}
                />
            )}

            <AlertDialog
                open={alertOpen}
                onOpenChange={setAlertOpen}
                message={alertMessage}
                status={reqStatus}
            />
        </div>
    );
}